import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { getAuthToken } from '../../services/api';
import './ParentSections.css';

const API_BASE_URL = process.env.REACT_APP_API_URL || '';

const ParentSectionControls = () => {
  const { t } = useTranslation('parent');
  const [selectedChild, setSelectedChild] = useState(null);
  const [children, setChildren] = useState([]);
  const [loading, setLoading] = useState(true);

  // Get user from localStorage
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const parentId = user.id;

  // Fetch children from API
  useEffect(() => {
    const fetchChildren = async () => {
      if (!parentId) {
        setLoading(false);
        return;
      }

      try {
        const token = getAuthToken();
        const response = await fetch(`${API_BASE_URL}/api/parent-dashboard/children/${parentId}`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });

        if (response.ok) {
          const data = await response.json();
          if (data.success) {
            setChildren(data.data);
          }
        }
      } catch (error) {
        console.error('Error fetching children:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchChildren();
  }, [parentId]);

  const [controls, setControls] = useState({});
  const [selectedControls, setSelectedControls] = useState(null);
  const [controlsLoading, setControlsLoading] = useState(false);

  // Fetch controls when child is selected
  useEffect(() => {
    const fetchControls = async () => {
      if (!selectedChild) {
        setSelectedControls(null);
        return;
      }

      setControlsLoading(true);
      try {
        const token = getAuthToken();
        const response = await fetch(`${API_BASE_URL}/api/parent-dashboard/controls/${selectedChild}`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });

        if (response.ok) {
          const data = await response.json();
          if (data.success) {
            setSelectedControls(data.data);
            setControls(prev => ({
              ...prev,
              [selectedChild]: data.data
            }));
          }
        }
      } catch (error) {
        console.error('Error fetching controls:', error);
      } finally {
        setControlsLoading(false);
      }
    };

    fetchControls();
  }, [selectedChild]);

  const updateControl = async (childId, category, key, value) => {
    // Update local state immediately
    const updatedControls = {
      ...controls[childId],
      [category]: {
        ...controls[childId][category],
        [key]: value
      }
    };

    setControls(prev => ({
      ...prev,
      [childId]: updatedControls
    }));

    if (selectedChild === childId) {
      setSelectedControls(updatedControls);
    }

    // Save to backend
    try {
      const token = getAuthToken();
      const response = await fetch(`${API_BASE_URL}/api/parent-dashboard/controls/${childId}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(updatedControls)
      });

      if (!response.ok) {
        const data = await response.json();
        console.error('Failed to update controls:', data.message);
        // Revert on error
        setControls(prev => ({
          ...prev,
          [childId]: prev[childId]
        }));
      }
    } catch (error) {
      console.error('Error updating controls:', error);
      // Revert on error
      setControls(prev => ({
        ...prev,
        [childId]: prev[childId]
      }));
    }
  };

  return (
    <div className="parent-main-content">
      <div className="parent-content-header">
        <h1 className="parent-content-title">{t('controls.title')}</h1>
        <p className="parent-content-subtitle">{t('controls.subtitle')}</p>
      </div>

      {/* Child Selector */}
      <div className="parent-section">
        <div className="parent-section-header">
          <h2 className="parent-section-title">{t('controls.selectChild')}</h2>
        </div>
        <div className="parent-child-selector">
          {children.map((child) => (
            <button
              key={child.id}
              className={`parent-child-select-btn ${selectedChild === child.id ? 'active' : ''}`}
              onClick={() => setSelectedChild(child.id)}
            >
              <span>{child.name}</span>
            </button>
          ))}
        </div>
      </div>

      {selectedControls && (
        <>
          {/* Content Restrictions */}
          <div className="parent-section">
            <div className="parent-section-header">
              <h2 className="parent-section-title">{t('controls.contentRestrictions')}</h2>
            </div>
            <div className="parent-controls-card">
              <div className="parent-control-item">
                <div className="parent-control-label">
                  <label>{t('controls.ageAppropriate')}</label>
                  <p className="parent-control-description">{t('controls.ageAppropriateDesc')}</p>
                </div>
                <div className="parent-toggle-switch">
                  <input
                    type="checkbox"
                    checked={selectedControls.contentRestrictions.ageAppropriate}
                    onChange={(e) => updateControl(selectedChild, 'contentRestrictions', 'ageAppropriate', e.target.checked)}
                  />
                  <span className="parent-toggle-slider"></span>
                </div>
              </div>

              <div className="parent-control-item">
                <label>{t('controls.maxDifficulty')}</label>
                <select
                  className="parent-control-select"
                  value={selectedControls.contentRestrictions.maxDifficulty}
                  onChange={(e) => updateControl(selectedChild, 'contentRestrictions', 'maxDifficulty', e.target.value)}
                >
                  <option value="beginner">{t('controls.beginner')}</option>
                  <option value="intermediate">{t('controls.intermediate')}</option>
                  <option value="advanced">{t('controls.advanced')}</option>
                </select>
              </div>

              <div className="parent-control-item">
                <label>{t('controls.allowedCategories')}</label>
                <div className="parent-checkbox-group">
                  {['Language', 'Math', 'Science', 'History', 'Art'].map((category) => (
                    <label key={category} className="parent-checkbox-item">
                      <input
                        type="checkbox"
                        checked={selectedControls.contentRestrictions.allowedCategories.includes(category)}
                        onChange={(e) => {
                          const current = selectedControls.contentRestrictions.allowedCategories;
                          const updated = e.target.checked
                            ? [...current, category]
                            : current.filter(c => c !== category);
                          updateControl(selectedChild, 'contentRestrictions', 'allowedCategories', updated);
                        }}
                      />
                      <span>{category}</span>
                    </label>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Time Limits */}
          <div className="parent-section">
            <div className="parent-section-header">
              <h2 className="parent-section-title">{t('controls.timeLimits')}</h2>
            </div>
            <div className="parent-controls-card">
              <div className="parent-control-item">
                <div className="parent-control-label">
                  <label>{t('controls.enableTimeLimits')}</label>
                  <p className="parent-control-description">{t('controls.enableTimeLimitsDesc')}</p>
                </div>
                <div className="parent-toggle-switch">
                  <input
                    type="checkbox"
                    checked={selectedControls.timeLimits.enabled}
                    onChange={(e) => updateControl(selectedChild, 'timeLimits', 'enabled', e.target.checked)}
                  />
                  <span className="parent-toggle-slider"></span>
                </div>
              </div>

              {selectedControls.timeLimits.enabled && (
                <>
                  <div className="parent-control-item">
                    <label>{t('controls.dailyLimit')}</label>
                    <div className="parent-input-group">
                      <input
                        type="number"
                        className="parent-control-input"
                        value={selectedControls.timeLimits.dailyLimit}
                        onChange={(e) => updateControl(selectedChild, 'timeLimits', 'dailyLimit', parseInt(e.target.value))}
                        min="30"
                        max="240"
                      />
                      <span className="parent-input-suffix">{t('controls.minutes')}</span>
                    </div>
                  </div>

                  <div className="parent-control-item">
                    <label>{t('controls.bedtime')}</label>
                    <input
                      type="time"
                      className="parent-control-input"
                      value={selectedControls.timeLimits.bedtime}
                      onChange={(e) => updateControl(selectedChild, 'timeLimits', 'bedtime', e.target.value)}
                    />
                  </div>
                </>
              )}
            </div>
          </div>

          {/* Communication Settings */}
          <div className="parent-section">
            <div className="parent-section-header">
              <h2 className="parent-section-title">{t('controls.communicationSettings')}</h2>
            </div>
            <div className="parent-controls-card">
              <div className="parent-control-item">
                <div className="parent-control-label">
                  <label>{t('controls.emailReports')}</label>
                  <p className="parent-control-description">{t('controls.emailReportsDesc')}</p>
                </div>
                <div className="parent-toggle-switch">
                  <input
                    type="checkbox"
                    checked={selectedControls.communication.emailReports}
                    onChange={(e) => updateControl(selectedChild, 'communication', 'emailReports', e.target.checked)}
                  />
                  <span className="parent-toggle-slider"></span>
                </div>
              </div>

              <div className="parent-control-item">
                <div className="parent-control-label">
                  <label>{t('controls.weeklyReports')}</label>
                  <p className="parent-control-description">{t('controls.weeklyReportsDesc')}</p>
                </div>
                <div className="parent-toggle-switch">
                  <input
                    type="checkbox"
                    checked={selectedControls.communication.weeklyReports}
                    onChange={(e) => updateControl(selectedChild, 'communication', 'weeklyReports', e.target.checked)}
                  />
                  <span className="parent-toggle-slider"></span>
                </div>
              </div>

              <div className="parent-control-item">
                <div className="parent-control-label">
                  <label>{t('controls.achievementAlerts')}</label>
                  <p className="parent-control-description">{t('controls.achievementAlertsDesc')}</p>
                </div>
                <div className="parent-toggle-switch">
                  <input
                    type="checkbox"
                    checked={selectedControls.communication.achievementAlerts}
                    onChange={(e) => updateControl(selectedChild, 'communication', 'achievementAlerts', e.target.checked)}
                  />
                  <span className="parent-toggle-slider"></span>
                </div>
              </div>

              <div className="parent-control-item">
                <div className="parent-control-label">
                  <label>{t('controls.progressUpdates')}</label>
                  <p className="parent-control-description">{t('controls.progressUpdatesDesc')}</p>
                </div>
                <div className="parent-toggle-switch">
                  <input
                    type="checkbox"
                    checked={selectedControls.communication.progressUpdates}
                    onChange={(e) => updateControl(selectedChild, 'communication', 'progressUpdates', e.target.checked)}
                  />
                  <span className="parent-toggle-slider"></span>
                </div>
              </div>
            </div>
          </div>

          {/* Privacy Settings */}
          <div className="parent-section">
            <div className="parent-section-header">
              <h2 className="parent-section-title">{t('controls.privacySettings')}</h2>
            </div>
            <div className="parent-controls-card">
              <div className="parent-control-item">
                <div className="parent-control-label">
                  <label>{t('controls.shareProgress')}</label>
                  <p className="parent-control-description">{t('controls.shareProgressDesc')}</p>
                </div>
                <div className="parent-toggle-switch">
                  <input
                    type="checkbox"
                    checked={selectedControls.privacy.shareProgress}
                    onChange={(e) => updateControl(selectedChild, 'privacy', 'shareProgress', e.target.checked)}
                  />
                  <span className="parent-toggle-slider"></span>
                </div>
              </div>

              <div className="parent-control-item">
                <div className="parent-control-label">
                  <label>{t('controls.publicProfile')}</label>
                  <p className="parent-control-description">{t('controls.publicProfileDesc')}</p>
                </div>
                <div className="parent-toggle-switch">
                  <input
                    type="checkbox"
                    checked={selectedControls.privacy.publicProfile}
                    onChange={(e) => updateControl(selectedChild, 'privacy', 'publicProfile', e.target.checked)}
                  />
                  <span className="parent-toggle-slider"></span>
                </div>
              </div>
            </div>
          </div>

          <div className="parent-section">
            <button className="parent-btn primary">
              <i className="material-icons">save</i>
              {t('controls.saveSettings')}
            </button>
          </div>
        </>
      )}

      {!selectedChild && (
        <div className="parent-empty-state">
          <i className="material-icons">person_outline</i>
          <p>{t('controls.selectChildFirst')}</p>
        </div>
      )}
    </div>
  );
};

export default ParentSectionControls;

